import { ref, computed, watch } from "vue"
import { defineStore } from "pinia"
import { api, getSafeURL } from "@/compositions/utils.ts"
import { useUserStore } from "@/stores/UserStore.ts"
import { lookupPM } from "@/compositions/strings.ts"

const developer = ref()
// Whose dashboard is loaded. Views outside /developer (the delivery page) read
// this to keep the developer nav pointed at the right person.
const loadedUsername = ref<string | null>(null)
const team = ref<any[]>([])
const activeProjects = ref<any[]>([])
const allProjects = ref<any[]>([])
const allProjectsUser = ref<string | null>(null)
const invoices = ref<{ pending: any[]; paid: any[] }>({ pending: [], paid: [] })
const invoicesUser = ref<string | null>(null)
const loading = ref(false)
const request = ref<any>(null)
// Submitted-but-not-yet-invoiced requests (last 2 weeks), shown on the dashboard as
// Activity cards until the PM converts each into a curdbee invoice.
const recentRequests = ref<any[]>([])
const savingRequest = ref(false)
const creatingInvoice = ref(false)
// Seeded by JobsPanel's Review button so DeveloperReviewView can avoid an
// extra API call. Shape: { jobid, review, language, projectname }.
const reviewTarget = ref<any>(null)

// Parsed `developer.apps` JSON. Each entry has a nested `crowdin` block with
// project metadata (slug, api id, owner, etc.). Icons stay raw here so they're
// never mutated (and thus never persisted expiring) — use `crowdinAppIcon()`
// for a display URL.
const crowdinApps = computed<any[]>(() => {
  if (!developer.value?.apps) return []
  try {
    const parsed = JSON.parse(developer.value.apps)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

// Centralized S3 icon presigning: the raw object URLs 403 without a signature,
// so we resolve them once and cache by raw URL. Consumers call crowdinAppIcon()
// instead of each presigning — and because the cache is keyed by URL (not by
// app object), it survives crowdinApps recomputes and never touches the raw
// app objects that get persisted via updateApps.
const resolvedIcons = ref<Record<string, string>>({})

function needsPresign(icon: any): icon is string {
  return typeof icon === "string" && icon.includes("amazonaws.com") && !icon.includes("X-Amz-")
}

watch(
  crowdinApps,
  (list) => {
    for (const app of list) {
      const icon = app?.icon
      if (needsPresign(icon) && !(icon in resolvedIcons.value)) {
        // Mark in-flight so we don't fire duplicate requests for the same URL.
        resolvedIcons.value = { ...resolvedIcons.value, [icon]: "" }
        getSafeURL(icon).then((url) => {
          if (typeof url === "string" && url) resolvedIcons.value = { ...resolvedIcons.value, [icon]: url }
        })
      }
    }
  },
  { immediate: true }
)

// Display URL for an app's icon: the presigned link once resolved, the URL
// as-is when it needs no signing (non-S3 or already signed), else "" while the
// signature is still in flight.
function crowdinAppIcon(app: any): string {
  const icon = app?.icon
  if (!icon || typeof icon !== "string") return ""
  if (!needsPresign(icon)) return icon
  return resolvedIcons.value[icon] || ""
}

// Client manages their own Crowdin source files when any app's owner is "m"
// (managed by client) or "r" (reseller). Unlocks the Crowdin Added/Updated reports.
const isCrowdinClient = computed(() => crowdinApps.value.some((a: any) => ["m", "r"].includes(a?.crowdin?.owner)))
const isPDFClient = computed(() => developer.value?.pdfclient === 1)

// Resolved PM for the loaded developer. Centralized so every panel doesn't
// re-implement the assignedby → PMs lookup with its own fallback string.
// Always returns a non-null entry (falls back to Benjamin via lookupPM).
const pm = computed(() => {
  const username = developer.value?.assignedby || ""
  const info = lookupPM(username)
  return { username, first: info.first, last: info.last, name: info.name, email: info.email }
})

export const useDeveloperStore = defineStore("DeveloperStore", () => {
  async function fill(username: string) {
    loading.value = true
    developer.value = null
    loadedUsername.value = null
    team.value = []
    activeProjects.value = []
    allProjects.value = []
    allProjectsUser.value = null
    invoices.value = { pending: [], paid: [] }
    invoicesUser.value = null
    request.value = null
    recentRequests.value = []
    let data = await api("getClient", "POST", { username })
    if (data && !data.error) {
      developer.value = data
      loadedUsername.value = username
      team.value = data.team || []
      fetchActiveProjects(username, true)
      fetchRecentRequests(username)
      fetchInProgressRequest()
    }
    loading.value = false
    return data
  }

  // Restore any in-progress (uninvoiced, untrashed) request for this user+client
  // so the dashboard can show "Finish request" and the request panel can resume
  // where the user left off after a reload or fresh login.
  async function fetchInProgressRequest() {
    const UserStore = useUserStore()
    const user = UserStore.user?.username
    const client = developer.value?.client
    if (!user || !client) return
    const data = await api("getInProgressRequest", "POST", { user, client })
    if (!data || data.error) return
    // Don't clobber an edit-target the user is actively working on. If
    // request.value is already an invoiced row (only an editing flow ever sets
    // customerinvoice on the local copy) and what we got back is a different
    // entry, keep what we have.
    if (request.value?.customerinvoice && request.value?.entry && request.value.entry !== data?.entry) return
    request.value = data || null
  }

  // Pull submitted-but-not-yet-invoiced requests (last 2 weeks) so the dashboard
  // can show Activity cards. Languages come back as the legacy "Name,short,full"
  // strings — pluck the third segment (full locale) for Flag rendering.
  async function fetchRecentRequests(username: string) {
    const rows = await api("getDeveloperRecentRequests", "POST", { username })
    if (!Array.isArray(rows)) {
      recentRequests.value = []
      return
    }
    recentRequests.value = rows.map((row: any) => {
      let langCodes: string[] = []
      if (row.languages) {
        try {
          const parsed = JSON.parse(row.languages)
          if (Array.isArray(parsed)) {
            langCodes = parsed.map((s: any) => (typeof s === "string" ? s.split(",")[2] || s.split(",")[0] : "")).filter(Boolean)
          }
        } catch {
          // Stored as a non-JSON string — leave langCodes empty
        }
      }
      return { ...row, langCodes, submittedAt: row.date }
    })
  }

  async function fetchActiveProjects(username: string, onlyrecent: boolean, force = false) {
    if (!onlyrecent && !force && allProjectsUser.value === username && allProjects.value.length) {
      return allProjects.value
    }
    let data = await api("getDeveloperProjects", "POST", { username, onlyrecent })
    if (data && !data.error) {
      if (onlyrecent) {
        activeProjects.value = data
      } else {
        allProjects.value = data
        allProjectsUser.value = username
      }
    }
    return data
  }

  // Persist any change to the in-progress translation request. On the first call
  // (no entry yet) the server creates a row; subsequent calls update it. The
  // returned row is mirrored into `request.value` so the UI stays in sync.
  async function upsertRequest(updates: Record<string, any> = {}) {
    if (!developer.value?.client) return null
    const UserStore = useUserStore()
    const payload: Record<string, any> = {
      ...(request.value || {}),
      ...updates,
      client: developer.value.client,
      user: request.value?.user || UserStore.user?.username,
      type: request.value?.type || updates.type || "task",
    }
    savingRequest.value = true
    try {
      const data = await api("upsertRequest", "POST", payload)
      if (data && !data.error) request.value = data
      return data
    } finally {
      savingRequest.value = false
    }
  }

  // Discard any in-progress request: trashes the DB row (if one was created) and
  // clears the local copy so the form starts fresh.
  async function resetRequest() {
    const entry = request.value?.entry
    request.value = null
    if (entry) {
      try {
        await api("upsertRequest", "POST", { entry, trash: 1 })
      } catch {
        // Swallow — the local state has already been cleared
      }
    }
  }

  // Build a curdbee invoice from the current request and return the new row. The
  // caller is responsible for navigating to the resulting invoice.
  async function createInvoice() {
    if (!request.value?.entry) {
      // Flush any pending state before creating the invoice
      const saved = await upsertRequest({})
      if (!saved?.entry) return null
    }
    creatingInvoice.value = true
    try {
      const data = await api("createInvoiceForRequest", "POST", { entry: request.value.entry })
      if (data && !data.error) {
        // Pull the updated request so customerinvoice is reflected locally
        request.value = { ...request.value, customerinvoice: data.customerinvoice }
      }
      return data
    } finally {
      creatingInvoice.value = false
    }
  }

  // Mirror a saved `apps` array into the developer record so the `crowdinApps`
  // computed re-derives and the cards re-render. Persistence happens in the card.
  function setApps(apps: any[]) {
    if (developer.value) developer.value = { ...developer.value, apps: JSON.stringify(apps) }
  }

  async function fetchInvoices(username: string, force = false) {
    if (!force && invoicesUser.value === username && (invoices.value.pending.length || invoices.value.paid.length)) {
      return invoices.value
    }
    let data = await api("getDeveloperInvoices", "POST", { username })
    if (data && !data.error) {
      invoices.value = { pending: data.pending || [], paid: data.paid || [] }
      invoicesUser.value = username
    }
    return data
  }

  return {
    developer,
    loadedUsername,
    team,
    activeProjects,
    allProjects,
    invoices,
    loading,
    request,
    recentRequests,
    fetchRecentRequests,
    fetchInProgressRequest,
    savingRequest,
    creatingInvoice,
    reviewTarget,
    crowdinApps,
    crowdinAppIcon,
    isCrowdinClient,
    isPDFClient,
    pm,
    fill,
    fetchActiveProjects,
    fetchInvoices,
    upsertRequest,
    resetRequest,
    createInvoice,
    setApps,
  }
})
